import { useEffect, useState } from "react";
import AppLayout from "../../layouts/AppLayout";
import Card from "../../components/Card";
import InfoCard from "../../components/InfoCard";
import useTradingOverview from "../../hooks/useTradingOverview";
import api from "../../api/axios";

const badge = (value) => ({ display: "inline-block", padding: "3px 8px", borderRadius: 999, fontSize: 12, fontWeight: 700, color: value === "SUCCESS" ? "#027a48" : "#b42318", background: value === "SUCCESS" ? "#ecfdf3" : "#fef3f2" });

function formatKoreanDateTime(value) {
    if (!value) return "시간 미상";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value).replace("T", " ").slice(5, 16);
    const parts = new Intl.DateTimeFormat("ko-KR", {
        timeZone: "Asia/Seoul", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", hour12: false
    }).formatToParts(date).reduce((result, part) => {
        result[part.type] = part.value;
        return result;
    }, {});
    return `${parts.month}월 ${parts.day}일 ${parts.hour}:${parts.minute}`;
}

function summarizeDeployment(item) {
    if (item.status === "SUCCESS") return "배포와 검증이 정상 완료되었습니다.";
    const source = item.errorSummary || item.codexResult || item.detail;
    return source ? String(source).split("\n")[0].slice(0, 180) : "처리 결과 요약이 없습니다.";
}

function TradeLog() {
    const { overview, error } = useTradingOverview();
    const [deployments, setDeployments] = useState([]);
    const [loadError, setLoadError] = useState("");
    useEffect(() => {
        api.get("/report/deployments").then((response) => setDeployments(response.data || []))
            .catch(() => setLoadError("자동배포 이력을 불러오지 못했습니다."));
    }, []);
    const dashboard = overview?.dashboard;
    const candidates = overview?.candidates || [];
    const realized = dashboard?.realizedProfitResponse || {};
    const totalSell = Number(realized.totSellAmt || 0);
    const profit = Number(realized.rlztPlLong || 0);
    const rate = totalSell > 0 ? (profit / totalSell) * 100 : 0;
    const pullbacks = candidates.filter((candidate) => String(candidate.reason || "").includes("PULLBACK")).length;

    return <AppLayout title="전략 리포트" subTitle="매매 성과와 자동배포 원인을 한눈에 확인합니다.">
        <div className="metric-grid">
            <InfoCard title="실현 손익" value={`${profit.toLocaleString()}원`} valueStyle={{ color: profit >= 0 ? "#027a48" : "#b42318" }} />
            <InfoCard title="실현 수익률" value={`${rate.toFixed(2)}%`} valueStyle={{ color: rate >= 0 ? "#027a48" : "#b42318" }} />
            <InfoCard title="오늘 후보" value={`${candidates.length}개`} sub={`눌림목 후보 ${pullbacks}개`} />
            <InfoCard title="현재 보유" value={`${(dashboard?.balance?.holdings || []).length}개`} sub="계좌 동기화 기준" />
        </div>
        <Card>
            <br/>
            <div className="card-title">자동배포 이력</div>
            <div className="info-sub">오류 감지 → Codex 수정 → 빌드 → 배포 → 서비스 재시작 결과입니다.</div>
            {loadError && <div className="operation-alert">{loadError}</div>}
            {!loadError && deployments.length === 0 && <div className="info-sub">자동배포 내역이 없습니다.</div>}
            {deployments.map((item) => <details key={item.deploymentId} style={{ marginTop: 12, padding: 14, border: "1px solid var(--input-border)", borderRadius: 10, background: "var(--card-bg)", color: "var(--text-color)" }}>
                <summary style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <span><strong>{formatKoreanDateTime(item.deployedAt)}</strong> · {item.triggerType || "CODEX"}</span>
                    <span style={badge(item.status)}>{item.status || "UNKNOWN"}</span>
                </summary>
                <div style={{ display: "grid", gap: 8, marginTop: 14 }}>
                    <div><strong>핵심 요약</strong><div className="info-sub" style={{ marginTop: 6 }}>{summarizeDeployment(item)}</div></div>
                    <div className="operation-row"><span>처리 단계</span><strong>React {item.frontBuildStatus || "-"} · Spring {item.backBuildStatus || "-"} · 서비스 {item.serviceStatus || "-"}</strong></div>
                    <div><strong>발생 원인</strong><pre style={{ whiteSpace: "pre-wrap", margin: "6px 0", color: "var(--text-color)" }}>{item.errorSummary || item.detail || "기록된 원인이 없습니다."}</pre></div>
                    <div><strong>오류 fingerprint</strong><code style={{ display: "block", marginTop: 6, wordBreak: "break-all" }}>{item.errorFingerprint || "-"}</code></div>
                    <div><strong>Codex 처리 결과</strong><pre style={{ whiteSpace: "pre-wrap", margin: "6px 0", color: "var(--text-color)" }}>{item.codexResult || "기록된 결과가 없습니다."}</pre></div>
                    <div><strong>상세 결과</strong><pre style={{ whiteSpace: "pre-wrap", margin: "6px 0", color: "var(--text-color)" }}>{item.detail || "-"}</pre></div>
                </div>
            </details>)}
        </Card>
        <Card>
            <div className="card-title">전략 상태</div>
            <div className="operation-row"><span>후보 선정</span><strong>코스닥·코스피·MA·RSI·수급·눌림목</strong></div>
            <div className="operation-row"><span>신규 매수</span><strong>장중 후보 신호 확인 후 실행</strong></div>
            <div className="operation-row"><span>지정가 학습</span><strong>실제 시장 상태와 수익률 기반 가중치</strong></div>
            <div className="operation-row"><span>위험 관리</span><strong>시장가·계좌 손실 제한 및 세력주 필터</strong></div>
        </Card>
        {error && <div className="operation-alert">{error}</div>}
    </AppLayout>;
}

export default TradeLog;
